import { Box, FormControl } from '@chakra-ui/react'
import { ContentNodeUtils, InputValueContentNode } from '@helo/cms'
import React, { useLayoutEffect, useRef, useState } from 'react'

import { DateTimeFilterInput } from '../../../gql/graphql'
import { RangeCalendar } from '../../Calendar/RangeCalendar'
import { SingleDateCalendar } from '../../Calendar/SingleDateCalendar'
import { americanToIso, isoToAmerican } from '../../inputs/DateInput/DateInput'
import { SelectInput } from '../../inputs/SelectInput/SelectInput'
import { TextInput } from '../../inputs/TextInput/TextInput'
import {
  ConditionOption,
  FilterEditorFormMethods,
} from '../StringFilterEditor/StringFilterEditor'
import {
  filterCriteriaToDateFilterInput,
  inputToDateFilterCriteria,
  isInRangeDateFilterValue,
  readDateRangeEndIso,
  readDateRangeStartIso,
} from '../dateFilterInputAdapters'

export interface DateFilterEditorProps {
  inputValueContentNode: InputValueContentNode
  conditionOptions: ConditionOption[]
  selectedField?: ConditionOption | undefined
  updateSelectedField?: (field: ConditionOption | undefined) => void
  selectedFieldRef?: React.MutableRefObject<ConditionOption | undefined>
  formMethods: FilterEditorFormMethods
  onUpdate?: (contentNode: InputValueContentNode) => void
  autoFocus?: boolean
  isDisabled?: boolean
  /** Called after a condition is selected, so parent can advance focus to the value input */
  onConditionSelected?: () => void
  /** When true (default), conditions are in dropdown and SingleDateCalendar has no pills; when false, legacy. */
  dateConditionsInDropdown?: boolean
  /** Click-outside boundary ID so selecting a date doesn't close a parent popover (e.g. filter editor). */
  clickBoundaryId?: string
  /** When set, calendar renders into this container (e.g. chip popover) so clicks stay "inside". */
  calendarContainerRef?: React.RefObject<HTMLElement | null>
  /** When true, Enter with condition menu open stops propagation so parent modal does not also handle Enter. */
  stopEnterPropagationWhenMenuOpen?: boolean
  /** When false, condition menu is closed (e.g. parent popover closed). */
  isFilterEditorOpen?: boolean
}

export type DateFilterEditorFields = {
  filterValue: string
}

const BLANK_KEY = 'blank'
const NOT_BLANK_KEY = 'notBlank'
const IN_RANGE_KEY = 'inRange'

export const DateFilterEditor = ({
  inputValueContentNode,
  conditionOptions,
  selectedField: selectedFieldProp,
  updateSelectedField: updateSelectedFieldProp,
  selectedFieldRef: selectedFieldRefProp,
  formMethods,
  onUpdate = () => {},
  autoFocus,
  isDisabled = false,
  onConditionSelected,
  dateConditionsInDropdown = true,
  clickBoundaryId,
  calendarContainerRef,
  stopEnterPropagationWhenMenuOpen,
  isFilterEditorOpen = true,
  ...props
}: DateFilterEditorProps) => {
  if (!formMethods) {
    throw new Error('DateFilterEditor requires formMethods')
  }

  const { register, setValue, watch } = formMethods

  const [localSelectedField, setLocalSelectedField] = useState<
    ConditionOption | undefined
  >(undefined)
  const localSelectedFieldRef = useRef<ConditionOption | undefined>(undefined)
  // Preserve the last non-empty value so it can be restored when switching from no-value conditions (blank/notBlank)
  const preservedValueRef = useRef<string>('')
  // Preserve the range "to" date (American format) when switching away from inRange so it can be restored
  const preservedToValueRef = useRef<string>('')

  const selectedField = selectedFieldProp ?? localSelectedField
  const selectedFieldRef = selectedFieldRefProp ?? localSelectedFieldRef
  const updateSelectedField =
    updateSelectedFieldProp ??
    ((field: ConditionOption | undefined) => {
      setLocalSelectedField(field)
      localSelectedFieldRef.current = field
    })

  const dataTypeConfig = inputValueContentNode.getDataTypeConfig()

  const withUpdatedData = (
    newValue: Record<string, any>,
  ): InputValueContentNode =>
    ContentNodeUtils.updateContentNode(newValue)(inputValueContentNode)

  const currentValue = inputValueContentNode.data as
    | Record<string, unknown>
    | undefined

  // For inRange, "to" date is stored in local state (form only has filterValue for "from")
  const filterTypeName = dataTypeConfig.ofType?.typeName

  const [filterToValueAmerican, setFilterToValueAmerican] = useState<string>(
    () => {
      const endIso = readDateRangeEndIso(currentValue, filterTypeName)
      return endIso ? isoToAmerican(endIso) : ''
    },
  )

  /** Get condition key from stored value (e.g. 'lessThan', 'greaterThan', 'equals'). Used to avoid overwriting stored value when Select syncs on open. */
  const getCurrentConditionKey = (): string | undefined => {
    if (!currentValue) return undefined
    if (isInRangeDateFilterValue(currentValue, filterTypeName))
      return IN_RANGE_KEY
    if (currentValue.equals === null) return BLANK_KEY
    if (
      (currentValue.not as Record<string, unknown> | undefined)?.equals === null
    )
      return NOT_BLANK_KEY
    if (typeof currentValue.lt === 'string') return 'lessThan'
    if (typeof currentValue.gt === 'string') return 'greaterThan'
    if (
      (currentValue.not as Record<string, unknown> | undefined)?.equals !==
        undefined &&
      (currentValue.not as Record<string, unknown> | undefined)?.equals !== null
    )
      return 'notEqual'
    if (currentValue.equals !== undefined && currentValue.equals !== null)
      return 'equals'
    return undefined
  }

  /** Get current date as ISO from stored value (any single-value key: lt, gt, equals, not.equals). Used to persist date when switching conditions. */
  const getCurrentDateIso = (): string => {
    if (!currentValue) return ''
    if (typeof currentValue.lt === 'string') return currentValue.lt
    if (typeof currentValue.gt === 'string') return currentValue.gt
    if (typeof currentValue.equals === 'string') return currentValue.equals
    const notObj = currentValue.not as Record<string, unknown> | undefined
    if (notObj?.equals !== undefined && typeof notObj.equals === 'string')
      return notObj.equals as string
    return ''
  }

  const getCurrentFilterValueAmerican = (
    effectiveField?: ConditionOption | undefined,
  ): string => {
    const field = effectiveField ?? selectedField
    if (!currentValue || !field) return ''

    const key = field.value
    if (key === BLANK_KEY || key === NOT_BLANK_KEY) return ''

    if (key === IN_RANGE_KEY) {
      const startIso = readDateRangeStartIso(currentValue, filterTypeName)
      return startIso ? isoToAmerican(startIso) : ''
    }

    // API keys for extended conditions (when dateConditionsInDropdown)
    if (key === 'lessThan' && typeof currentValue.lt === 'string')
      return isoToAmerican(currentValue.lt)
    if (key === 'greaterThan' && typeof currentValue.gt === 'string')
      return isoToAmerican(currentValue.gt)
    if (key === 'notEqual') {
      const notObj = currentValue.not as Record<string, unknown> | undefined
      if (notObj?.equals !== undefined && typeof notObj.equals === 'string')
        return isoToAmerican(notObj.equals)
    }

    if (currentValue[key] !== undefined && currentValue[key] !== null) {
      const val = currentValue[key]
      if (typeof val === 'string') return isoToAmerican(val)
    }

    if (key === 'equals') {
      const notObj = currentValue.not as Record<string, unknown> | undefined
      if (notObj?.equals !== undefined) {
        const v = notObj.equals
        if (typeof v === 'string') return isoToAmerican(v)
      }
    }

    return ''
  }

  register('filterValue')
  const watchedValue = watch('filterValue')

  // Sync form value from filter state before paint to avoid placeholder flicker when changing condition
  useLayoutEffect(() => {
    const effectiveField =
      conditionOptions.find(opt => opt.value === getCurrentConditionKey()) ??
      selectedField
    const american = getCurrentFilterValueAmerican(effectiveField)
    setValue('filterValue', american)
    if (effectiveField?.value === IN_RANGE_KEY) {
      const endIso = readDateRangeEndIso(currentValue, filterTypeName)
      if (endIso) {
        setFilterToValueAmerican(isoToAmerican(endIso))
      }
    }
  }, [
    selectedField?.value,
    inputValueContentNode.data,
    setValue,
    filterTypeName,
  ])

  const isNoValueCondition =
    selectedField?.value === BLANK_KEY || selectedField?.value === NOT_BLANK_KEY
  const isRangeCondition = selectedField?.value === IN_RANGE_KEY
  const isValueDisabled = isDisabled || !selectedField

  // Resolve option from stored data so Select has correct value on chip open (selectedField from parent can be stale on first paint)
  const resolvedSelectedField =
    conditionOptions.find(opt => opt.value === getCurrentConditionKey()) ??
    selectedField

  const filterCriteria = inputToDateFilterCriteria(
    currentValue as DateTimeFilterInput | undefined,
    filterTypeName,
  )

  const handleCalendarUpdate = (criteria: {
    filterValue?: string
    filterToValue?: string
    condition?: string
    [key: string]: unknown
  }) => {
    // Use selectedFieldRef (always current) to determine condition instead of
    // the criteria.condition from SingleDateCalendar's callback, which can be
    // stale due to React closure capture (e.g. onBlur fires with old condition
    // after a condition change).
    const currentCondition =
      selectedFieldRef.current?.value ?? criteria.condition
    const dateTimeInput = filterCriteriaToDateFilterInput(
      {
        ...criteria,
        condition: currentCondition,
      },
      filterTypeName,
    )
    onUpdate(withUpdatedData(dateTimeInput as Record<string, any>))
  }

  return (
    <>
      <SelectInput
        data-testid="date-filter-condition-select"
        variant="filterCondition"
        options={conditionOptions}
        placeholder="Search or select condition"
        isDisabled={isDisabled}
        filterMode="substring"
        menuBoundaryId={clickBoundaryId}
        stopEnterPropagationWhenMenuOpen={stopEnterPropagationWhenMenuOpen}
        menuIsOpen={isFilterEditorOpen ? undefined : false}
        onChange={(option: { value: string } | null) => {
          if (!option) return
          const selected = conditionOptions.find(
            opt => opt.value === option.value,
          )
          if (selected) {
            // Preserve the current value before switching (only from value-requiring conditions)
            if (
              selectedField?.requiresValue !== false &&
              selectedField?.value !== BLANK_KEY &&
              selectedField?.value !== NOT_BLANK_KEY
            ) {
              const currentTrimmed = (watchedValue || '').trim()
              if (currentTrimmed) {
                preservedValueRef.current = currentTrimmed
              }
              if (selectedField?.value === IN_RANGE_KEY) {
                const toTrimmed = filterToValueAmerican?.trim()
                if (toTrimmed) {
                  preservedToValueRef.current = toTrimmed
                }
              }
            }
            updateSelectedField(selected)
            onConditionSelected?.()
            queueMicrotask(() => {
              const currentFormValue = watchedValue || ''
              const key = selected.value
              const dateValueAmerican =
                currentFormValue.trim() || preservedValueRef.current
              const dateValueIso = dateValueAmerican
                ? americanToIso(dateValueAmerican)
                : ''
              const toAmerican =
                key === IN_RANGE_KEY
                  ? filterToValueAmerican?.trim() ||
                    preservedToValueRef.current.trim() ||
                    dateValueAmerican
                  : ''
              if (key === IN_RANGE_KEY && toAmerican) {
                setFilterToValueAmerican(toAmerican)
              }
              const dateTimeInput = filterCriteriaToDateFilterInput(
                {
                  condition: key as any,
                  columnId: '',
                  filterValue:
                    key === IN_RANGE_KEY
                      ? dateValueIso || ''
                      : key === BLANK_KEY || key === NOT_BLANK_KEY
                        ? ''
                        : dateValueIso || '',
                  filterToValue:
                    key === IN_RANGE_KEY
                      ? toAmerican
                        ? americanToIso(toAmerican)
                        : ''
                      : undefined,
                },
                filterTypeName,
              )
              onUpdate(withUpdatedData(dateTimeInput as Record<string, any>))
            })
          }
        }}
        value={selectedField ?? resolvedSelectedField}
        flex="1"
      />
      {conditionOptions.length > 0 && (
        <FormControl flex="1" isDisabled={isValueDisabled}>
          {!selectedField ? (
            <TextInput placeholder="Search or input value" disabled flex="1" />
          ) : isNoValueCondition ? (
            <TextInput placeholder="No value needed" disabled flex="1" />
          ) : isRangeCondition ? (
            <Box
              flex="1"
              minWidth={0}
              pointerEvents={isValueDisabled ? 'none' : undefined}
              opacity={isValueDisabled ? 0.6 : 1}
            >
              <RangeCalendar
                filterCriteria={filterCriteria}
                onUpdate={handleCalendarUpdate}
                columnId=""
                autoFocus={autoFocus}
                clickBoundaryId={clickBoundaryId}
                containerRef={calendarContainerRef}
              />
            </Box>
          ) : (
            <Box
              flex="1"
              minWidth={0}
              pointerEvents={isValueDisabled ? 'none' : undefined}
              opacity={isValueDisabled ? 0.6 : 1}
            >
              <SingleDateCalendar
                filterCriteria={{
                  ...filterCriteria,
                  condition:
                    getCurrentConditionKey() ??
                    selectedField?.value ??
                    filterCriteria.condition,
                }}
                onUpdate={handleCalendarUpdate}
                columnId=""
                showConditionPills={!dateConditionsInDropdown}
                clickBoundaryId={clickBoundaryId}
                containerRef={calendarContainerRef}
              />
            </Box>
          )}
        </FormControl>
      )}
    </>
  )
}

DateFilterEditor.displayName = 'DateFilterEditor'
